import { getState } from '../store';
import { getElapsedSec } from '../utils/time-utils';

/**
 * Timer component.
 * Computes remaining time from Date.now() - locationStartedAt.
 * No setInterval drift — recalculates on every tick and on visibilitychange.
 *
 * onTimeout is fired exactly once per createTimer instance, on the first
 * animation frame where remaining hits zero. Fired via queueMicrotask so we
 * don't re-enter the rAF loop from inside update(). Destroying the timer
 * before it has fired cancels the callback.
 */
export function createTimer(
  container: HTMLElement,
  timeLimitSec: number,
  onTimeout?: () => void
): { destroy: () => void } {
  const el = document.createElement('div');
  el.className = 'text-timer-hero';
  el.style.textAlign = 'center';
  container.appendChild(el);

  let rafId = 0;
  let fired = false;
  let destroyed = false;

  function update(): void {
    const state = getState();
    if (!state.locationStartedAt) {
      el.textContent = formatTime(timeLimitSec);
      el.style.color = 'var(--success)';
      el.classList.remove('timer-pulse', 'timer-pulse-fast');
      rafId = requestAnimationFrame(update);
      return;
    }

    const elapsed = getElapsedSec(state.locationStartedAt);
    const remaining = Math.max(0, timeLimitSec - elapsed);
    const ratio = remaining / timeLimitSec;

    el.textContent = remaining > 0 ? formatTime(remaining) : '+' + formatTime(elapsed - timeLimitSec);

    // Color states from DESIGN.md
    if (remaining <= 0) {
      el.style.color = 'var(--danger)';
      el.classList.remove('timer-pulse', 'timer-pulse-fast');
      if (!fired && onTimeout) {
        fired = true;
        queueMicrotask(() => { if (!destroyed) onTimeout(); });
      }
    } else if (ratio < 0.25) {
      el.style.color = 'var(--danger)';
      el.classList.remove('timer-pulse');
      el.classList.add(remaining < 30 ? 'timer-pulse-fast' : 'timer-pulse');
    } else if (ratio < 0.5) {
      el.style.color = 'var(--amber)';
      el.classList.remove('timer-pulse', 'timer-pulse-fast');
    } else {
      el.style.color = 'var(--success)';
      el.classList.remove('timer-pulse', 'timer-pulse-fast');
    }

    rafId = requestAnimationFrame(update);
  }

  rafId = requestAnimationFrame(update);

  return {
    destroy() {
      destroyed = true;
      cancelAnimationFrame(rafId);
      el.remove();
    }
  };
}

function formatTime(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}
